import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// --- Period filter (set by PeriodPicker, persisted in localStorage) ---
// The picker is the single source of truth: it writes "noam_period" and
// dispatches "noam:period-changed". This interceptor injects a ?period=<label>
// query string on every GET to /workspace/* and /executive/* so backend
// endpoints can return data filtered to that period.
const PERIOD_PATHS = ["/workspace/", "/executive/"];
const NON_FILTERABLE = new Set([
  "/workspace/me",
  "/workspace/organisation",
  "/workspace/organisations",
  "/workspace/plan",
  "/workspace/systems",
]);

function readPeriodLabel() {
  try {
    const raw = localStorage.getItem("noam_period");
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s) return null;
    if (s.mode === "year") return `${s.year}`;
    if (s.mode === "quarter") return `T${s.quarter} ${s.year}`;
    if (s.mode === "month") {
      const m = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"][s.month];
      return `${m} ${s.year}`;
    }
    return null;
  } catch {
    return null;
  }
}

api.interceptors.request.use((config) => {
  try {
    if ((config.method || "get").toLowerCase() !== "get") return config;
    const url = config.url || "";
    const matchesPeriodScope = PERIOD_PATHS.some((p) => url.includes(p));
    if (!matchesPeriodScope) return config;
    // Never override an explicit caller-set ?period= param
    const callerParams = config.params || {};
    if (callerParams.period) return config;
    // Skip endpoints where a period filter would be nonsensical
    if ([...NON_FILTERABLE].some((p) => url.includes(p))) return config;
    const label = readPeriodLabel();
    if (label) {
      config.params = { ...callerParams, period: label };
    }
  } catch { /* ignore — best effort */ }
  return config;
});


/**
 * Upload helper for multipart/form-data requests.
 *
 * The shared `api` instance defaults to `Content-Type: application/json`.
 * For multipart uploads we MUST let axios derive the boundary itself —
 * if we pass `multipart/form-data` without a boundary, FastAPI cannot
 * parse the body. This helper ensures the header is removed for the
 * single request without polluting global defaults.
 *
 * Use cases : Annuaire import, future Noam Drive uploads, attachments
 * on any module's "Joindre un document" button.
 *
 * @param {string} url           Relative path (e.g. `/workspace/documents`)
 * @param {FormData} formData    The multipart body
 * @param {object} [options]     Extra axios options (params, onUploadProgress…)
 * @returns {Promise<AxiosResponse>}
 *
 * @example
 *   const fd = new FormData();
 *   fd.append("file", file);
 *   const r = await apiUpload("/workspace/annuaire/import/preview", fd, {
 *     params: { raw_text: text },
 *     onUploadProgress: (e) => setPct(Math.round((e.loaded / e.total) * 100)),
 *   });
 */
export function apiUpload(url, formData, options = {}) {
  return api.post(url, formData, {
    ...options,
    headers: {
      ...(options.headers || {}),
      // Force axios to compute the multipart boundary from the FormData.
      "Content-Type": undefined,
    },
  });
}
